import Icon from '@ant-design/icons';
import { useMemoizedFn } from 'ahooks';
import { Modal, message } from 'antd';
import 'cropperjs/dist/cropper.css';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactCropperElement } from 'react-cropper';
import Cropper from 'react-cropper';

import RecallSvg from '@/components/Icon/svgs/recall.svg';
import RetrySvg from '@/components/Icon/svgs/retry.svg';
import { useTranslation } from '@/i18n/client';
import { coverToVideo, updateHomepageSettings } from '@/service/homePage';
import { useHomePageStore } from '@/store/useHomepageStore';
import reportClick, { EReportClickKey } from '@/utils/report/reportClick';
import handleUpload from '@/utils/upload';
import { imageTypeList, isWeChat } from '@/utils/utils';

interface IProps {
  handleSetCoverCb?: (cover: string) => void;
  handleSetVideoCb?: (url: string) => void;
  reset?: boolean;
  cropperModalClassName?: string;
  inMobile?: boolean;
  initialCover?: string;
  initialVideo?: string;
}

const defaultCover =
  'https://mindverseglobal.mindverse.com/front-img/crm/1759133775950/20250929-161600.png';

const useImage2VideoCropper = ({
  handleSetCoverCb,
  handleSetVideoCb,
  cropperModalClassName,
  inMobile = false,
  reset = false,
  initialCover = '',
  initialVideo = ''
}: IProps) => {
  const { t } = useTranslation();

  const [cropperModalTitle, setCropperModalTitle] = useState<string>(t('homepage.cover.cover'));
  const [uploadAvatarFile, setUploadAvatarFile] = useState<string>('');
  const [cropModalOpen, setCropModalOpen] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [videoLoading, setVideoLoading] = useState<boolean>(false);
  const [showCropper, setShowCropper] = useState<boolean>(true);
  const [previewImg, setPreviewImg] = useState<string>('');
  const [previewVideo, setPreviewVideo] = useState<string>('');
  const [resetToCropper, setResetToCropper] = useState<boolean>(false);
  const [reGeneratevideo, setReGeneratevideo] = useState<boolean>(false);
  const [showUseIt, setShowUseIt] = useState<boolean>(false);
  const cropperRef = useRef<ReactCropperElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const timerRef = useRef<NodeJS.Timeout>();
  const [videoLoadingRemainTime, setVideoLoadingRemainTime] = useState<number>(60);

  // 用于追踪组件挂载状态
  const isMountedRef = useRef<boolean>(true);

  // 组件卸载时清理
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setPreviewImg(initialCover);

    if (initialCover) {
      setShowCropper(false);
      setCropperModalTitle(t('homepage.cover.cover'));
    }
  }, [initialCover, t]);

  useEffect(() => {
    setPreviewVideo(initialVideo);

    if (initialVideo) {
      setShowCropper(false);
      setReGeneratevideo(true);
      setCropperModalTitle(t('homepage.cover.cover'));
    }
  }, [initialVideo, t]);

  const openCropperModal = useMemoizedFn(() => {
    if (!initialCover && !uploadAvatarFile) {
      if (inputRef.current) inputRef.current.value = '';

      inputRef.current?.click();

      return;
    }

    setResetToCropper(false);
    setCropModalOpen(true);
  });

  const onReady = () => {
    const cropper = cropperRef.current?.cropper;

    if (!cropper) return;

    const img = cropper.getImageData(); // 获取原图数据
    const container = cropper.getContainerData(); // 容器大小

    // 计算 contain 比例
    const fitRatio = Math.min(
      container.width / img.naturalWidth,
      container.height / img.naturalHeight
    );

    // 用 zoomTo（如果版本支持）
    if (typeof cropper.zoomTo === 'function') {
      cropper.zoomTo(fitRatio);
    } else {
      // 兼容：直接用 setCanvasData 手动调宽高
      const canvas = cropper.getCanvasData();
      const currentRatio = img.width / img.naturalWidth;
      const ratio = fitRatio / currentRatio;

      cropper.setCanvasData({
        ...canvas,
        width: canvas.width * ratio,
        height: canvas.height * ratio
      });
    }
  };

  // 文件选择处理
  const onAvatarSelect: React.ChangeEventHandler<HTMLInputElement> = useMemoizedFn((event) => {
    const fileList = event.target.files ?? [];

    if (fileList.length === 0) {
      return;
    }

    const file = fileList[0];

    if (!imageTypeList.includes(file.type)) {
      message.error('File type not allowed!');

      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      if (reader.result) {
        setUploadAvatarFile(reader.result as string);
        setPreviewImg('');
        setShowCropper(true);
        setCropModalOpen(true);
        setCropperModalTitle(t('homepage.cover.cover'));
      }
    };
    reader.readAsDataURL(file);
    setCropModalOpen(true);
  });

  // 图片上传处理（支持头像和封面）
  const handleUploadImage = useMemoizedFn(() => {
    if (isUploading) return;

    setIsUploading(true);
    message.success('Uploading...');

    const canvas = cropperRef.current?.cropper.getCroppedCanvas();

    if (!canvas) {
      setIsUploading(false);

      return;
    }

    const uploadStartTime = Date.now();

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setIsUploading(false);
          message.error('Image processing failed');

          return;
        }

        const fileExt = 'jpg';
        const fileName = `${'cover'}_${Date.now()}.${fileExt}`;
        const uploadPath = 'users/homepage_cover';
        const file = new File([blob], fileName, { type: `image/jpeg` });

        const temptTime = Date.now();
        const cutDuration = temptTime - uploadStartTime;

        handleUpload(file, uploadPath)
          .then((res) => {
            const uploadEndTime = Date.now();

            const uploadDuration = uploadEndTime - uploadStartTime;

            console.log('Upload details:', {
              fileName,
              fileSize: `${(blob.size / 1024).toFixed(2)} KB`,
              duration: `${uploadDuration}ms`,
              cutDuration: `${cutDuration}ms`
            });

            reportClick(EReportClickKey.homepage_cover_upload, {
              key1: uploadDuration,
              key2: `${(blob.size / 1024).toFixed(2)} KB`,
              key3: res,
              key4: cutDuration
            });

            handleSetCoverCb?.(res);
            setCropperModalTitle(t('homepage.cover.cover'));
            setShowUseIt(false);
            setReGeneratevideo(false);

            if (reset) {
              setPreviewImg(res);
            } else {
              setCropModalOpen(false);
              setShowCropper(true);
              setPreviewImg('');
            }

            setPreviewVideo('');
          })
          .catch((error) => {
            const _uploadEndTime = Date.now();

            const _uploadDuration = _uploadEndTime - uploadStartTime;

            message.error(error?.message ?? 'Upload failed');
            reportClick(EReportClickKey.homepage_cover_upload_failed, {
              key1: _uploadDuration,
              key2: `${(blob.size / 1024).toFixed(2)} KB`,
              key3: '',
              key4: cutDuration
            });
          })
          .finally(() => {
            if (reset) {
              setShowCropper(false);
            }

            setResetToCropper(false);
            setIsUploading(false);
          });
      },
      'image/jpeg',
      0.85
    );
  });

  const handleSelectImage = useMemoizedFn(() => {
    if (inputRef.current) inputRef.current.value = '';

    inputRef.current?.click();
  });

  const handleCoverToVideo = useMemoizedFn(() => {
    setVideoLoading(true);
    setCropperModalTitle(t('homepage.cover.generateLiveCover'));
    setVideoLoadingRemainTime(60);
    timerRef.current = setInterval(() => {
      setVideoLoadingRemainTime((prev) => {
        if (prev > 0) {
          return prev - 1;
        }

        clearInterval(timerRef.current);

        return 0;
      });
    }, 1000);

    coverToVideo({ url: previewImg })
      .then((res) => {
        if (res.data.code !== 0) {
          message.error(res.data.message || 'Generate video failed');

          return;
        }

        const videoUrl = res.data.data.videoUrl;

        setPreviewVideo(videoUrl);

        // 检查组件是否已卸载
        // 如果已卸载，自动保存视频 URL
        if (!isMountedRef.current) {
          console.log('Component was unmounted during video generation, auto-saving video...');
          updateHomepageSettings({ video: videoUrl }).then((_res) => {
            if (_res.data.code === 0) {
              useHomePageStore.getState().updateHomepage({ video: videoUrl });
            }
          });
        }
      })
      .then(() => {
        // 只有在组件仍然挂载时才更新 UI 状态
        if (isMountedRef.current) {
          setShowUseIt(true);
          setReGeneratevideo(false);
        }
      })
      .finally(() => {
        setVideoLoading(false);
        clearInterval(timerRef.current);
        setVideoLoadingRemainTime(60);
      });
  });

  const handleCloseModal = useMemoizedFn(() => {
    setCropModalOpen(false);
    setResetToCropper(false);
    setUploadAvatarFile('');
    setShowUseIt(false);
    setReGeneratevideo(false);

    if (inputRef.current) inputRef.current.value = '';

    if (initialCover || initialVideo) {
      setShowCropper(false);
      setPreviewImg(initialCover);
      setPreviewVideo(initialVideo);
    } else {
      setShowCropper(true);
      setPreviewImg('');
      setPreviewVideo('');
    }
  });

  const FileInput = useMemo(() => {
    return (
      <input
        ref={inputRef}
        accept="image/png, image/jpeg"
        className="hidden"
        disabled={isUploading}
        onChange={onAvatarSelect}
        type="file"
      />
    );
  }, [onAvatarSelect, isUploading]);

  const CropperModal = useMemo(() => {
    return (
      <Modal
        cancelText={t('common.cancel')}
        centered
        className={cropperModalClassName}
        confirmLoading={isUploading}
        footer={previewImg ? null : undefined}
        okText={t('common.confirm')}
        onCancel={handleCloseModal}
        onOk={() => {
          handleUploadImage();
        }}
        open={cropModalOpen}
        title={<div className="text-base leading-7 -mt-3 mb-2">{cropperModalTitle}</div>}
      >
        {showCropper ? (
          <Cropper
            ref={cropperRef}
            aspectRatio={9 / 16}
            autoCropArea={1}
            background={false}
            center={true}
            checkOrientation={false}
            cropBoxResizable={true}
            dragMode={inMobile ? 'move' : 'crop'}
            guides={true}
            minCropBoxHeight={10}
            minCropBoxWidth={10}
            preview=".img-preview"
            ready={onReady}
            responsive={true}
            src={uploadAvatarFile}
            style={{ height: 400, width: '100%' }}
            viewMode={1}
            zoomTo={1}
          />
        ) : (
          <div className="flex flex-col items-center w-full">
            <div className="relative overflow-hidden">
              {previewVideo ? (
                <video
                  autoPlay
                  className={'h-[320px] w-[180px]'}
                  loop
                  muted
                  playsInline
                  src={previewVideo}
                  webkit-playsinline="true"
                  x5-video-player-type="h5"
                />
              ) : (
                <img alt="" className={'object-cover h-[320px] w-[180px]'} src={previewImg} />
              )}

              {videoLoading && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 6,
                    background:
                      'linear-gradient(180deg, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.7) 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {/* 在这里有一根横条，在loading的过程中上下移动 */}
                  <div
                    className="w-full absolute animate-pulse"
                    style={{
                      animation: 'moveVertical 5s infinite',
                      background:
                        'linear-gradient(270deg, rgba(254, 253, 255, 0.2) 0%, #FEFDFF 50%, rgba(254, 253, 255, 0.2) 100%)',
                      height: '2px'
                    }}
                  />
                  <div className="flex flex-col items-center gap-2">
                    {videoLoadingRemainTime > 0 ? (
                      <span className="text-[#fefdff] text-[12px] leading-[20px] text-center">
                        {t('homepage.cover.generating')} <br />
                        {`${t('homepage.cover.estimatedApprox')} ${videoLoadingRemainTime}s`}
                      </span>
                    ) : (
                      <span className="text-[#fefdff] text-[12px] leading-[20px] text-center">
                        {t('homepage.cover.takingALittleLonger')}
                      </span>
                    )}
                    <div className="text-[#fefdff] mt-2 text-[12px] leading-[20px] text-center">
                      {t('homepage.cover.autoSave')}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-col justify-center items-center w-full">
              {isWeChat() || inMobile ? null : (
                <div className="flex w-full gap-[16px] text-[#fefdff] mt-4">
                  {showUseIt && (
                    <div
                      className="w-[36px] h-[36px] flex items-center justify-center text-[15px] cursor-pointer"
                      onClick={() => {
                        setShowUseIt(false);
                        setReGeneratevideo(true);
                        handleCoverToVideo();
                      }}
                      style={{ background: '#7B67EF', borderRadius: '100%' }}
                    >
                      <Icon component={RetrySvg} />
                    </div>
                  )}

                  {showUseIt ? (
                    <div
                      className="flex-1 rounded-[18px] py-[6px] leading-[24px] text-[14px] flex items-center justify-center gap-[8px] "
                      onClick={() => {
                        handleSetVideoCb?.(previewVideo);
                        handleCloseModal();
                      }}
                      style={{
                        backgroundColor: '#7B67EF',
                        cursor: 'pointer'
                      }}
                    >
                      {t('homepage.cover.useIt')}
                    </div>
                  ) : previewImg !== defaultCover ? (
                    <div
                      className="flex-1 rounded-[18px] py-[6px] leading-[24px] text-[14px] flex items-center justify-center gap-[8px] "
                      onClick={videoLoading ? undefined : handleCoverToVideo}
                      style={{
                        backgroundColor: videoLoading ? '#A99DF5' : '#7B67EF',
                        cursor: videoLoading ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {reGeneratevideo ? (
                        <Icon component={RetrySvg} />
                      ) : (
                        <Icon component={RecallSvg} />
                      )}
                      {videoLoading
                        ? t('homepage.cover.generating')
                        : reGeneratevideo
                          ? t('homepage.cover.regenerateLiveCover')
                          : t('homepage.cover.generateLiveCover')}
                    </div>
                  ) : null}
                </div>
              )}
              {!videoLoading && (
                <div className="flex flex-col mt-[16px] py-[6px] w-full gap-[12px]">
                  <div
                    className="bg-[rgba(254,253,255,0.4)] text-[#7B67EF] w-full rounded-[24px] text-center py-[8px] leading-[24px] text-[14px] font-medium cursor-pointer shadow-[0px_4px_32px_0px_rgba(194,158,107,0.08)]"
                    onClick={() => {
                      handleSelectImage();
                    }}
                  >
                    {t('homepage.cover.replaceImage')}
                  </div>
                  <div className="text-[#756C5D] text-center text-[14px] font-normal leading-[24px]">
                    {t('homepage.cover.availableInApp')}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {/* shit TODO: 用文件承接 */}
        <style>
          {`
      @keyframes moveVertical {
        0% {
          top: 0%;
        }
        50% {
          top: 100%;
        }
        100% {
          top: 0%;
        }
      }
    `}
        </style>
      </Modal>
    );
  }, [
    t,
    cropperModalClassName,
    isUploading,
    previewImg,
    handleCloseModal,
    cropModalOpen,
    cropperModalTitle,
    showCropper,
    inMobile,
    uploadAvatarFile,
    previewVideo,
    videoLoading,
    videoLoadingRemainTime,
    showUseIt,
    handleCoverToVideo,
    reGeneratevideo,
    handleUploadImage,
    handleSetVideoCb,
    handleSelectImage
  ]);

  return {
    FileInput,
    CropperModal,
    isUploading,
    openCropperModal,
    resetToCropper
  };
};

export default useImage2VideoCropper;
